import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { API } from './constants';

// Asset picker dialog — lets admins choose a brand-asset image or video for content
// `mediaFilter`: "image" (default) | "video" | "all"
export const AssetPickerDialog = ({ open, onClose, onSelect, mediaFilter = 'image' }) => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios
      .get(`${API}/admin/brand-assets`, { withCredentials: true })
      .then((res) => {
        const filtered = res.data.filter((a) => {
          const ct = a.content_type || '';
          if (mediaFilter === 'image') return ct.startsWith('image/');
          if (mediaFilter === 'video') return ct.startsWith('video/');
          return ct.startsWith('image/') || ct.startsWith('video/');
        });
        setAssets(filtered);
      })
      .catch(() => toast.error('Failed to load assets'))
      .finally(() => setLoading(false));
  }, [open, mediaFilter]);

  const dialogTitle =
    mediaFilter === 'video' ? 'Choose Video from Library' :
    mediaFilter === 'all' ? 'Choose Asset from Library' :
    'Choose Image from Library';
  const emptyMsg =
    mediaFilter === 'video' ? 'No video assets uploaded yet. Go to Brand Assets to add one.' :
    mediaFilter === 'all' ? 'No image or video assets uploaded yet.' :
    'No image assets uploaded yet. Go to Brand Assets to add one.';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-white">{dialogTitle}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-white/40 text-sm">Loading…</div>
        ) : assets.length === 0 ? (
          <div className="py-10 text-center text-white/40 text-sm" data-testid="asset-picker-empty">
            {emptyMsg}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-[60vh] overflow-y-auto pr-2">
            {assets.map((asset) => {
              const publicUrl = `${API}/brand-assets/${asset.id}`;
              const posterUrl = asset.poster_url ? `${window.location.origin}${asset.poster_url}` : null;
              const isVideo = (asset.content_type || '').startsWith('video/');
              return (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => onSelect(publicUrl, { posterUrl, isVideo })}
                  className="bg-black border border-white/10 hover:border-[#D4AF37] transition-colors text-left group"
                  data-testid={`asset-picker-item-${asset.id}`}
                >
                  <div className="aspect-square flex items-center justify-center p-4 border-b border-white/10 relative">
                    {isVideo ? (
                      posterUrl ? (
                        <img
                          src={posterUrl}
                          alt={asset.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      ) : (
                        <video
                          src={publicUrl}
                          muted
                          playsInline
                          preload="metadata"
                          className="max-w-full max-h-full object-contain"
                        />
                      )
                    ) : (
                      <img
                        src={publicUrl}
                        alt={asset.name}
                        className="max-w-full max-h-full object-contain"
                      />
                    )}
                    {isVideo && (
                      <span className="absolute top-2 right-2 text-[10px] uppercase tracking-widest text-[#D4AF37] bg-black/70 border border-[#D4AF37]/40 px-2 py-0.5">
                        Video
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-white text-sm truncate">{asset.name}</p>
                    <p className="text-[#D4AF37] text-[10px] uppercase tracking-widest mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      Click to select
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
